import React, { useCallback, useEffect, useState } from 'react';
import config from '../config';
import authService from '../services/authService';

// Vendor-side controls for a selected customer: toggle which products the customer
// may self-manage keys for (with the plan their self-issued keys inherit), and
// invite a customer-org user to log in to the portal.
export default function CustomerSelfServicePanel({ customerGuid, customerName }) {
  const [products, setProducts] = useState([]);
  const [plansByProduct, setPlansByProduct] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingId, setSavingId] = useState(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  const headers = useCallback(
    () => ({ 'Content-Type': 'application/json', ...authService.getAuthHeaders() }),
    []
  );

  const load = useCallback(async () => {
    if (!customerGuid) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/customers/${customerGuid}/product-access`,
        { headers: headers() }
      );
      const body = await res.json();
      if (!res.ok || body.success === false) {
        throw new Error(body.error || 'Failed to load self-service settings');
      }
      const prods = body.products || [];
      setProducts(prods);
      // Lazily fetch plan options for each product so the vendor can pick the fallback.
      const entries = await Promise.all(
        prods.map(async (p) => {
          try {
            const r = await fetch(
              `${config.apiBaseUrl}/api/plans?productId=${p.productId}`,
              { headers: headers() }
            );
            const d = await r.json();
            return [p.productId, d.plans || []];
          } catch {
            return [p.productId, []];
          }
        })
      );
      setPlansByProduct(Object.fromEntries(entries));
    } catch (err) {
      setError(err.message || 'Failed to load self-service settings');
    } finally {
      setLoading(false);
    }
  }, [customerGuid, headers]);

  useEffect(() => {
    setInviteLink('');
    load();
  }, [load]);

  const saveAccess = async (productId, next) => {
    setSavingId(productId);
    setError('');
    setSuccess('');
    // Optimistic update.
    setProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, ...next } : p))
    );
    const current = products.find((p) => p.productId === productId) || {};
    const merged = { ...current, ...next };
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/customers/${customerGuid}/product-access`,
        {
          method: 'PUT',
          headers: headers(),
          body: JSON.stringify({
            productId,
            enabled: !!merged.enabled,
            planId: merged.planId || null
          })
        }
      );
      const body = await res.json();
      if (!res.ok || body.success === false) {
        throw new Error(body.error || 'Failed to save');
      }
      setSuccess('Self-service access updated.');
    } catch (err) {
      setError(err.message || 'Failed to save');
      load(); // revert to server truth
    } finally {
      setSavingId(null);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError('');
    setSuccess('');
    setInviteLink('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/customers/${customerGuid}/invites`,
        { method: 'POST', headers: headers(), body: JSON.stringify({ email: inviteEmail.trim() }) }
      );
      const body = await res.json();
      if (!res.ok || body.success === false) {
        throw new Error(body.error || 'Failed to send invite');
      }
      setSuccess(`Invite sent to ${inviteEmail.trim()}.`);
      setInviteLink(`${window.location.origin}/invite/${body.inviteToken}`);
      setInviteEmail('');
    } catch (err) {
      setError(err.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mt-6">
      <h2 className="text-lg font-semibold text-slate-100">Self-service access</h2>
      <p className="text-sm text-slate-400 mt-1 mb-4">
        Choose which products <strong>{customerName}</strong> can self-manage keys for, and invite
        a user to log in. Self-issued keys use the plan from the customer's existing key for that
        product, or the fallback plan below when they have none yet.
      </p>

      {error && (
        <div className="bg-red-950/30 border border-red-800/50 text-red-300 px-4 py-2 rounded mb-3 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-950/30 border border-emerald-800/50 text-emerald-300 px-4 py-2 rounded mb-3 text-sm">
          {success}
        </div>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm">Loading…</p>
      ) : products.length === 0 ? (
        <p className="text-slate-500 text-sm italic">No products to grant.</p>
      ) : (
        <div className="space-y-2 mb-6">
          {products.map((p) => (
            <div
              key={p.productId}
              className="flex items-center justify-between gap-4 bg-slate-900/50 border border-slate-700 rounded px-4 py-2"
            >
              <label className="flex items-center gap-3 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={!!p.enabled}
                  disabled={savingId === p.productId}
                  onChange={(e) => saveAccess(p.productId, { enabled: e.target.checked })}
                  className="accent-indigo-500"
                />
                {p.productName}
              </label>
              <select
                className="p-1.5 bg-slate-900 rounded border border-slate-700 text-slate-200 text-sm [&>option]:bg-slate-800 focus:outline-none focus:border-slate-600 disabled:opacity-50"
                value={p.planId || ''}
                disabled={savingId === p.productId}
                onChange={(e) => saveAccess(p.productId, { planId: e.target.value || null })}
              >
                <option value="">Fallback plan…</option>
                {(plansByProduct[p.productId] || []).map((pl) => (
                  <option key={pl.id} value={pl.id}>
                    {pl.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-700 pt-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-2">Invite a user</h3>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            placeholder="person@customer.com"
            className="flex-grow min-w-[220px] p-2 bg-slate-900 rounded border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600 text-sm"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') sendInvite();
            }}
          />
          <button
            onClick={sendInvite}
            disabled={inviting || !inviteEmail.trim()}
            className="py-2 px-4 bg-indigo-600 rounded hover:bg-indigo-500 disabled:opacity-50 transition-colors text-white font-medium text-sm"
          >
            {inviting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
        {inviteLink && (
          <p className="text-xs text-slate-400 mt-2 break-all">
            Invite link: <span className="font-mono text-slate-300">{inviteLink}</span>
          </p>
        )}
      </div>
    </div>
  );
}
